import { selectNotifications } from "Redux/Notifications/slice";
import { FORMAT_MONTH_DATE_YEAR } from "Shared/Constants/app";
import {
  INotification,
  NOTIFICATIONS_APP_NAME,
} from "Shared/Types/notifications";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useSelector } from "react-redux";

export const useNotification = () => {
  const notifications = useSelector(selectNotifications);

  const getMonday = (date: Date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  const dateRange = useMemo(() => {
    const today = new Date();
    const monday = getMonday(today);

    const lastSunday = new Date(monday);
    lastSunday.setDate(monday.getDate() - 1);
    lastSunday.setDate(lastSunday.getDate() + 1);

    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);
    lastMonday.setDate(lastMonday.getDate());

    const created_before = new Date(today);
    created_before.setDate(created_before.getDate() + 1);

    return {
      created_before: created_before.toISOString(),
      created_after: lastMonday.toISOString(),
    };
  }, []);

  function isLastWeek(dateString: string) {
    const inputDate = new Date(dateString);
    const today = new Date();
    const currentMonday = new Date(today);
    currentMonday.setDate(
      today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)
    );
    currentMonday.setHours(0, 0, 0, 0);
    return currentMonday > inputDate;
  }

  const getDatedNotifications = (notifications: INotification[]) => {
    const res: {
      day: {
        number: number;
        title: string;
      };
      list: INotification[];
    }[] = [];
    notifications.forEach((item) => {
      const day = new Date(item.created).getUTCDate();
      const idx = res.findIndex((el) => {
        if (isLastWeek(item.created)) return el.day.title === "Last Week";
        return el.day.number === day;
      });

      if (idx >= 0) res[idx].list.push(item);
      else {
        const date = dayjs(item.created).startOf("day");
        const today = dayjs().startOf("day");
        const title = isLastWeek(item.created)
          ? "Last Week"
          : date.isSame(today)
            ? "Today"
            : date.format(FORMAT_MONTH_DATE_YEAR);

        res.push({
          day: {
            number: day,
            title,
          },
          list: [item],
        });
      }
    });
    return res
      .sort((a, b) => b.day.number - a.day.number)
      .map((item) => ({
        ...item,
        list: getCategorizedNotifications(item.list),
      }));
  };

  const getCategorizedNotifications = (notifications: INotification[]) => {
    const res: {
      app_name: NOTIFICATIONS_APP_NAME;
      list: INotification[];
    }[] = [];
    notifications.forEach((item) => {
      const idx = res.findIndex((el) => el.app_name === item.app_name);
      if (idx >= 0) res[idx].list.push(item);
      else {
        res.push({
          app_name: item.app_name,
          list: [item],
        });
      }
    });
    return res;
  };

  const notificationsList = useMemo(
    () => getDatedNotifications(notifications.results),
    [notifications.results]
  );

  return {
    dateRange,
    notificationsList,
  };
};
